import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { RestaurantForm } from "./RestaurantForm";
import { CUISINES, emptyRestaurant } from "./constants";
import { toast } from "sonner";

export function RestaurantsTab({ restaurants, reload }) {
  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);

  const openCreate = () => { setEditing(structuredClone(emptyRestaurant)); setFormOpen(true); };
  const openEdit = (r) => { setEditing(structuredClone(r)); setFormOpen(true); };

  const saveRestaurant = async () => {
    try {
      const payload = { ...editing };
      // Sync cuisine_ar from cuisine_en
      payload.cuisine_ar = CUISINES.find((c) => c.id === payload.cuisine_en)?.ar || payload.cuisine_en;
      payload.rating = Number(payload.rating);
      payload.delivery_fee = Number(payload.delivery_fee);
      payload.min_order = Number(payload.min_order);
      payload.cities = payload.cities || [];
      payload.menu = payload.menu.map((m) => ({ ...m, price: Number(m.price) }));
      if (payload.id) {
        await api.put(`/admin/restaurants/${payload.id}`, payload);
        toast.success("تم التحديث");
      } else {
        await api.post("/admin/restaurants", payload);
        toast.success("تمت الإضافة");
      }
      setFormOpen(false);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الحفظ");
    }
  };

  const deleteRestaurant = async (r) => {
    if (!confirm(`حذف ${r.name_ar}?`)) return;
    try {
      await api.delete(`/admin/restaurants/${r.id}`);
      toast.success("تم الحذف");
      await reload();
    } catch (e) {
      toast.error("فشل الحذف");
    }
  };

  return (
    <>
      <div className="mb-4 flex justify-between items-center">
        <p className="text-sm text-[#6B6B6B]">إدارة المطاعم والقوائم</p>
        <Button
          onClick={openCreate}
          className="rounded-full bg-[#E05D3A] hover:bg-[#C24B2C] text-white"
          data-testid="admin-add-restaurant"
        >
          <Plus className="w-4 h-4 me-2" />
          إضافة مطعم
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {restaurants.map((r) => (
          <div key={r.id} className="jh-card overflow-hidden" data-testid={`admin-rest-${r.id}`}>
            <div className="aspect-video bg-[#F0F0F0] relative">
              {r.image && <img src={r.image} alt="" className="w-full h-full object-cover" />}
              <div className="absolute top-2 end-2 bg-black/70 text-white text-xs rounded-full px-2 py-0.5">
                ⭐ {r.rating}
              </div>
            </div>
            <div className="p-4">
              <h3 className="font-bold text-[#1A1A1A] truncate">{r.name_ar}</h3>
              <p className="text-xs text-[#6B6B6B] mt-0.5">{r.cuisine_ar} · {r.menu?.length || 0} صنف</p>
              <div className="mt-3 flex gap-2">
                <Button size="sm" variant="outline" onClick={() => openEdit(r)} className="flex-1 rounded-full" data-testid={`admin-edit-${r.id}`}>
                  <Pencil className="w-3.5 h-3.5 me-1" /> تعديل
                </Button>
                <Button size="sm" variant="outline" onClick={() => deleteRestaurant(r)} className="rounded-full text-red-600 hover:bg-red-50" data-testid={`admin-delete-${r.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

    <Dialog open={formOpen} onOpenChange={setFormOpen}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing?.id ? "تعديل مطعم" : "إضافة مطعم جديد"}</DialogTitle>
        </DialogHeader>
        {editing && (
          <RestaurantForm editing={editing} setEditing={setEditing} />
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setFormOpen(false)}>إلغاء</Button>
          <Button onClick={saveRestaurant} className="bg-[#E05D3A] hover:bg-[#C24B2C] text-white" data-testid="admin-save-restaurant">حفظ</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
